/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { BasicCard } from '@coursera-web/cds-labs';
import type { EnterpriseSkills_Skillset as SkillSet } from '__generated__/graphql-types-do-not-use';

import a11yKeyPress from 'js/lib/a11yKeyPress';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography, Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';
import { TextTruncate, css as cuiCss, placeholder } from '@coursera/coursera-ui';
import type { SectionName } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { toProgramSkillSet } from 'bundles/enterprise-legacy-learner-home/links';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { ObjectiveListQuery_EnterpriseTargetSkillProfileSummariesV1Resource_byProgram_elements as TargetSkillProficiencySummary } from 'bundles/program-home/components/multiprogram/__generated__/ObjectiveListQuery';
import GoldStarSvg from 'bundles/skills-common/private/star-symbol/GoldStarSvg';

import _t from 'i18n!nls/program-home';

const MAX_SKILL_ITEMS = 4 as const;

type BaseSkillSetCardProps = {
  skillSummary: TargetSkillProficiencySummary | SkillSet;
  skillSetOrder: number;
  filterTrackingData?: string[];
};

type SkillSetCardContentProps = BaseSkillSetCardProps &
  ({ isOrgScoped: true; orgSlug: string; programSlug?: never } | { isOrgScoped?: false; programSlug: string });

const getIsTSP = (
  skillSummary: TargetSkillProficiencySummary | SkillSet
): skillSummary is TargetSkillProficiencySummary => {
  return (skillSummary as TargetSkillProficiencySummary).targetSkillProficiencies !== undefined;
};

const styles = {
  fullHeightContainer: css`
    height: 100%;

    // Child tracked div
    > div {
      height: 100%;
    }
  `,
  cardHoverAnimation: css`
    --card-box-shadow: 0 0 40px -8px rgb(0 0 0 / 16%), 0 0 24px -16px rgb(0 0 0 / 16%);
    --card-hover-transform: scale3d(1.03, 1.03, 1.08) translate3d(0.1rem, -0.25rem, 20rem);

    transition: all 0.3s cubic-bezier(0, 0, 0.5, 1);
    transform: translateZ(0);

    &:hover {
      box-shadow: var(--card-box-shadow);
      transform: var(--card-hover-transform);
    }
  `,
  skillsetContainer: css`
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-spacing-200);
    overflow: hidden;
    cursor: pointer;
    height: 100%;
  `,
  headerContainer: css`
    position: relative;
    padding: var(--cds-spacing-200) var(--cds-spacing-300);
    background-color: var(--cds-color-emphasis-quaternary-background-xstrong);
    height: 126px;
    overflow: hidden;

    /*
      This is a hack to prevent a visual issue with the animation.

      At the time of writing this, there would be a white gap on the
      right side of the component when the hover animation has been
      activated and reactivated.

      If this no longer occurs, please feel free to remove this.
    */
    &::before {
      position: absolute;
      left: -5px;
      right: -5px;
      top: -5px;
      bottom: -5px;
      background-color: var(--cds-color-emphasis-quaternary-background-xstrong);
      content: '';
      z-index: -1;
    }
  `,
  headerTitle: css`
    display: flex;
    align-items: center;
  `,
  skillsetHeader: css`
    margin-left: var(--cds-spacing-150);
    color: var(--cds-color-neutral-stroke-primary-weak);
  `,
  skillsetName: css`
    margin-top: var(--cds-spacing-150);
    max-height: 50px;
  `,
  detailsContainer: css`
    padding: var(--cds-spacing-200) var(--cds-spacing-300);
  `,
  gainMessage: css`
    margin-bottom: var(--cds-spacing-50);
  `,
  skillsList: css`
    list-style: none;
    padding: 0;
    margin-bottom: 0;
  `,
  listItemContainer: css`
    display: flex;
    align-items: center;
    margin-bottom: var(--cds-spacing-50);
  `,
  listBullet: css`
    margin-right: var(--cds-spacing-50);
  `,
  listItem: css`
    display: flex;
    width: calc(100% - 20px);
  `,
  itemText: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  moreTrailer: css`
    margin-left: var(--cds-spacing-50);
    font-weight: bold;
    flex-shrink: 0;
  `,
};

/**
 * Note on tracking:
 * Only the container is clickable or focusable in this component to avoid an A11y error for nested interactive elements.
 * However we still want to track clicks to to the "fake" link. The skillset card will also register a click
 * in these instances but we can also subtract the subclass click count from the card click count to get the accurate count.
 */
export const SkillSetCardV3 = ({
  skillSummary,
  skillSetOrder,
  filterTrackingData,
  ...scopeProps
}: SkillSetCardContentProps) => {
  const isTSP = getIsTSP(skillSummary);
  const track = useTracker();

  const href = scopeProps.isOrgScoped
    ? `/organizations/${scopeProps.orgSlug}/skillsets/${skillSummary.slug}`
    : toProgramSkillSet(scopeProps.programSlug, skillSummary.slug);
  const { id } = skillSummary;
  const order = skillSetOrder > 0 ? skillSetOrder - 1 : skillSetOrder;

  const eventingV3SkillSetData = {
    skillsetCard: {
      index: order,
    },
    pageSection: {
      sectionName: 'cta_banner' as SectionName,
      index: 3,
    },
    ...(filterTrackingData && {
      filter: filterTrackingData,
    }),
    skillset: {
      id,
    },
  };
  const onSkillSetContainerClick = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      redirectClientOrServer(href);

      track('click_skillset_card', eventingV3SkillSetData);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [href]
  );

  const skillsetCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_skillset_card',
    eventingV3SkillSetData
  );
  return (
    <div ref={skillsetCardRef} css={styles.fullHeightContainer}>
      <BasicCard
        component={TrackedDiv}
        data-testid="skillset-card-container"
        css={styles.skillsetContainer}
        role="link"
        trackingName="skillset_container"
        trackClicks
        withVisibilityTracking
        data={{
          skillSetOrder,
          skillSetId: skillSummary.id,
          objectId: scopeProps.isOrgScoped
            ? `skillset~${scopeProps.orgSlug}/${skillSummary.slug}`
            : `skillset~${scopeProps.programSlug}/${skillSummary.slug}`,
          skillsetSlug: skillSummary.slug,
          ...(scopeProps.isOrgScoped ? { orgSlug: scopeProps.orgSlug } : { programSlug: scopeProps.programSlug }),
          tab: 'catalog-tab',
        }}
        data-e2e="program-home-catalog-skillset-container"
        onClick={onSkillSetContainerClick}
        onKeyPress={(event: React.KeyboardEvent<HTMLElement>) => a11yKeyPress(event, onSkillSetContainerClick)}
      >
        <div css={styles.headerContainer}>
          <div css={styles.headerTitle}>
            <GoldStarSvg width={31} height={36} />
            <Typography2
              display="inline"
              css={styles.skillsetHeader}
              component="div"
              variant="actionSecondary"
              color="invertBody"
            >
              {_t('SKILLSET')}
            </Typography2>
          </div>
          <Typography2 css={styles.skillsetName} variant="subtitleLarge" component="h3" color="invertBody">
            <TextTruncate line={2} text={isTSP ? skillSummary.title : skillSummary.name} />
          </Typography2>
        </div>
        <div css={styles.detailsContainer}>
          <Typography2 variant="subtitleMedium" css={styles.gainMessage} component="div">
            <FormattedMessage
              message={_t('Gain {numSkills, plural, =1 {# skill} other {# skills}}:')}
              numSkills={
                isTSP
                  ? skillSummary.targetSkillProficiencies.length
                  : (skillSummary.skillProficiencyTargets?.totalCount ?? 0)
              }
            />
          </Typography2>
          <ul css={styles.skillsList}>
            {isTSP
              ? skillSummary.targetSkillProficiencies.slice(0, MAX_SKILL_ITEMS).map((skill, index) => {
                  return (
                    <li key={skill.skillId} css={styles.listItemContainer}>
                      <CheckIcon css={styles.listBullet} size="medium" />
                      <Typography2 className="list-item" component="div">
                        <span css={styles.itemText}>{skill.skillName}</span>
                        {index === 3 && skillSummary.targetSkillProficiencies.length > MAX_SKILL_ITEMS && (
                          <span css={styles.moreTrailer}>
                            <FormattedMessage
                              message={_t(' + {numSkills} more')}
                              numSkills={skillSummary.targetSkillProficiencies.length - MAX_SKILL_ITEMS}
                            />
                          </span>
                        )}
                      </Typography2>
                    </li>
                  );
                })
              : skillSummary.skillProficiencyTargets?.edges
                  ?.map((_) => _.node)
                  .slice(0, MAX_SKILL_ITEMS)
                  .map((skill, index) => {
                    return (
                      <li key={skill.id} css={styles.listItemContainer}>
                        <CheckIcon css={styles.listBullet} size="medium" />
                        <Typography2 className="list-item" component="div">
                          <span css={styles.itemText}>{skill.skillName}</span>
                          {index === 3 && (skillSummary.skillProficiencyTargets?.totalCount ?? 0) > MAX_SKILL_ITEMS && (
                            <span css={styles.moreTrailer}>
                              <FormattedMessage
                                message={_t(' + {numSkills} more')}
                                numSkills={(skillSummary.skillProficiencyTargets?.totalCount ?? 0) - MAX_SKILL_ITEMS}
                              />
                            </span>
                          )}
                        </Typography2>
                      </li>
                    );
                  })}
          </ul>
        </div>
      </BasicCard>
    </div>
  );
};

export const SkillSetCardV3Placeholder = () => {
  return (
    <div className="skillset-container" aria-hidden data-testid="SkillSetCardV2Placeholder">
      <div className="header-container" aria-hidden>
        <div className="header-title" aria-hidden>
          <GoldStarSvg width={31} height={36} />
          <Typography2
            display="inline"
            className="skillset-header"
            component="div"
            variant="subtitleMedium"
            color="invertBody"
          >
            {_t('SKILLSET')}
          </Typography2>
        </div>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography
          className={cuiCss(placeholder.styles.shimmer, 'skillset-name').className}
          variant="h2semibold"
          component="div"
          color="invertBody"
          aria-hidden
        >
          &nbsp;
        </Typography>
      </div>
      <div className="details-container" aria-hidden>
        <div className={cuiCss(placeholder.styles.shimmer, 'gain-message').className}>&nbsp;</div>
        <ul className="skills-list" aria-hidden>
          {[1, 2, 3].map((value) => {
            return (
              <li key={value} css={styles.listItemContainer}>
                <CheckIcon css={styles.listBullet} size="small" />
                <Typography2
                  className={cuiCss(placeholder.styles.shimmer, 'list-item').className}
                  component="div"
                  aria-hidden
                >
                  <span css={styles.itemText}>&nbsp;</span>
                </Typography2>
              </li>
            );
          })}
        </ul>
        <Typography2
          component="h3"
          className={cuiCss(placeholder.styles.shimmer, 'link-section').className}
          variant="subtitleMedium"
        >
          &nbsp;
        </Typography2>
      </div>
    </div>
  );
};
